import {
  type APISlideAttributes,
  type ArdoqId,
  type EnhancedPresentation,
  isDashboardSlide,
  isMetamodelSlide,
  isReportSlide,
  isSpecificMetamodelSlide,
  isVisualizationSlide,
  type VisualizationSlide,
  ViewIds,
} from '@ardoq/api-types';
import type { NonEmptyArray } from '@ardoq/common-helpers';
import {
  asapScheduler,
  concatMap,
  defer,
  from,
  map,
  type Observable,
  of,
  subscribeOn,
  take,
  toArray,
} from 'rxjs';
import { slideOperations } from 'streams/slides/slideOperations';

type PlanPlaceholderReason =
  | 'unauthorized'
  | 'workspace-mode'
  | 'scenario-missing'
  | 'not-exportable-view'
  | 'type-not-supported';

export type CapturePlaceholderReason =
  | 'failed'
  | 'slide-load-failed'
  | 'not-ready'
  | 'no-exporter'
  | 'timeout'
  | 'busy'
  | 'no-image'
  | 'too-large';

export type PlaceholderReason =
  | PlanPlaceholderReason
  | CapturePlaceholderReason;

export type SlideImage = { dataUrl: string; w: number; h: number };

export type SlideContent =
  | { kind: 'image'; images: NonEmptyArray<SlideImage> }
  | { kind: 'placeholder'; reason: PlaceholderReason };

export type CaptureTarget =
  | { type: 'visualization'; view: ViewIds }
  | {
      type: 'scenario';
      view: ViewIds;
      scenarioId: ArdoqId;
      isInVisualDiffMode: boolean;
    }
  | { type: 'metamodel'; metamodelId: ArdoqId }
  | { type: 'html' };

export type SlidePlan =
  | { kind: 'capture'; target: CaptureTarget }
  | { kind: 'placeholder'; reason: PlanPlaceholderReason };

export type PlannedSlide = {
  id: ArdoqId;
  title: string;
  description: string;
  plan: SlidePlan;
};

export type DeckRow = {
  title: string;
  description: string;
  content: SlideContent;
};

export type SlideProgress = {
  slideNumber: number;
  total: number;
  title: string;
};

export type SlideToCapture = { id: ArdoqId; target: CaptureTarget };

export type ToRenderedViewId = (view: ViewIds) => ViewIds;

export type SlideClassifiers = {
  toRenderedViewId: ToRenderedViewId;
  hasScenario: (scenarioId: ArdoqId) => boolean;
};

type CaptureSlides = {
  slides: PlannedSlide[];
  captureSlide$: (slide: SlideToCapture) => Observable<SlideContent>;
  onSlideStarted: (progress: SlideProgress) => void;
};

type PlannedSlideSources = {
  presentation: EnhancedPresentation | null;
  slidesById: Partial<Record<ArdoqId, APISlideAttributes>>;
  titleById: Map<ArdoqId, string>;
  classifiers: SlideClassifiers;
};

export const NOT_EXPORTABLE_VIEWS = new Set([
  ViewIds.ASSISTANT_DIAGRAM,
  ViewIds.ASSISTANT_BLOCKS,
]);

const EXPORTABLE_SCENARIO_VIEWS = new Set([ViewIds.BLOCK_DIAGRAM]);

const placeholder = (reason: PlanPlaceholderReason): SlidePlan => ({
  kind: 'placeholder',
  reason,
});

const capture = (target: CaptureTarget): SlidePlan => ({
  kind: 'capture',
  target,
});

const classifyScenarioSlide = (
  slide: VisualizationSlide,
  scenarioId: ArdoqId,
  hasScenario: SlideClassifiers['hasScenario']
): SlidePlan => {
  if (!EXPORTABLE_SCENARIO_VIEWS.has(slide.view)) {
    return placeholder('not-exportable-view');
  }
  if (!hasScenario(scenarioId)) {
    return placeholder('scenario-missing');
  }
  return capture({
    type: 'scenario',
    view: slide.view,
    scenarioId,
    isInVisualDiffMode: Boolean(slide.isInVisualDiffMode),
  });
};

export const classifySlide = (
  slide: APISlideAttributes | undefined,
  { toRenderedViewId, hasScenario }: SlideClassifiers
): SlidePlan => {
  if (!slide) {
    return placeholder('unauthorized');
  }
  if (isMetamodelSlide(slide) && isSpecificMetamodelSlide(slide)) {
    return capture({ type: 'metamodel', metamodelId: slide.metamodelId });
  }
  if (isDashboardSlide(slide) || isReportSlide(slide)) {
    return capture({ type: 'html' });
  }
  if (!isVisualizationSlide(slide)) {
    return placeholder('type-not-supported');
  }
  if (NOT_EXPORTABLE_VIEWS.has(slide.view)) {
    return placeholder('not-exportable-view');
  }
  if (slide.scenarioId) {
    return classifyScenarioSlide(slide, slide.scenarioId, hasScenario);
  }
  if (!slideOperations.isViewpointSlide(slide)) {
    return placeholder('workspace-mode');
  }
  return capture({
    type: 'visualization',
    view: toRenderedViewId(slide.view),
  });
};

export const toPlannedSlides = ({
  presentation,
  slidesById,
  titleById,
  classifiers,
}: PlannedSlideSources): PlannedSlide[] =>
  (presentation?.slides ?? []).map(id => ({
    id,
    title: titleById.get(id) ?? '',
    description: slidesById[id]?.description ?? '',
    plan: classifySlide(slidesById[id], classifiers),
  }));

const toContent$ = (
  { id, plan }: PlannedSlide,
  captureSlide$: CaptureSlides['captureSlide$']
): Observable<SlideContent> =>
  plan.kind === 'capture'
    ? captureSlide$({ id, target: plan.target })
    : of({ kind: 'placeholder', reason: plan.reason });

/**
 * Captures the slides one at a time and emits the deck rows once all of them
 * are captured. Unsubscribing cancels the slide being captured. Each slide
 * starts after the action that ended the previous one.
 */
export const captureSlides$ = ({
  slides,
  captureSlide$,
  onSlideStarted,
}: CaptureSlides): Observable<DeckRow[]> =>
  from(slides).pipe(
    concatMap((slide, index) =>
      defer(() => {
        onSlideStarted({
          slideNumber: index + 1,
          total: slides.length,
          title: slide.title,
        });
        return toContent$(slide, captureSlide$).pipe(take(1));
      }).pipe(
        subscribeOn(asapScheduler),
        map((content): DeckRow => ({
          title: slide.title,
          description: slide.description,
          content,
        }))
      )
    ),
    toArray()
  );
